/**
 * Presentation helpers — pure, deterministic. Times are Central Africa Time
 * (Zambia, UTC+2, no DST), day-first with en-US short months, exactly as the
 * PDF and the public verify page print them (src/verify/format.ts), so a
 * time on screen equals the time on the file.
 */
import type { CheckName, CheckStatus, EntityKind, Purpose } from './types.js';

export const TIME_ZONE = 'Africa/Lusaka';
export const TIME_ZONE_LABEL = 'CAT';

const dateTime = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const dateOnly = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const timeOnly = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const weekday = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  weekday: 'long',
});

function parts(f: Intl.DateTimeFormat, d: Date): (t: string) => string {
  const p = f.formatToParts(d);
  return (t) => p.find((x) => x.type === t)?.value ?? '';
}

function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d, 12));
  }
  const d = typeof v === 'string' ? new Date(v) : v;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `19 Sep 2026 14:03 CAT` */
export function formatInstant(v: string | Date | null | undefined): string {
  const d = toDate(v);
  if (!d) return '—';
  const get = parts(dateTime, d);
  return `${get('day')} ${get('month')} ${get('year')} ${get('hour')}:${get('minute')} ${TIME_ZONE_LABEL}`;
}

/** `19 Sep 2026` */
export function formatDate(v: string | Date | null | undefined): string {
  const d = toDate(v);
  if (!d) return '—';
  const get = parts(dateOnly, d);
  return `${get('day')} ${get('month')} ${get('year')}`;
}

/** `14:02` */
export function formatTime(v: string | Date | null | undefined): string {
  const d = toDate(v);
  if (!d) return '—';
  const get = parts(timeOnly, d);
  return `${get('hour')}:${get('minute')}`;
}

function dayKey(d: Date): string {
  const get = parts(dateOnly, d);
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** `today 14:03` · `yesterday` · `Monday` · `19 Sep 2026` — the artboards' recency. */
export function formatRecency(
  v: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toDate(v);
  if (!d) return '—';
  const diffDays = Math.round((dayStart(now) - dayStart(d)) / 86_400_000);
  if (diffDays === 0) return `today ${formatTime(d)}`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays > 1 && diffDays < 7) return weekday.format(d);
  return formatDate(d);
}

function dayStart(d: Date): number {
  return Date.parse(`${dayKey(d)} 00:00:00 GMT+0200`);
}

/** `read 14:02 today` / `read 18 Sep 2026 02:14` */
export function formatRead(
  v: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toDate(v);
  if (!d) return 'not read';
  return dayKey(d) === dayKey(now)
    ? `read ${formatTime(d)} today`
    : `read ${formatInstant(d)}`;
}

const KEEP_UPPER = new Set([
  'PLC',
  'LLC',
  'CC',
  'SA',
  'AG',
  'BV',
  'NV',
  'UK',
  'USA',
  'ZM',
  'ZRA',
  'PACRA',
]);

/** Registered names arrive upper-case; show them as names. */
export function titleCase(name: string): string {
  return name
    .toLowerCase()
    .split(/(\s+|-|\/|\(|\))/)
    .map((w) => {
      const up = w.toUpperCase();
      if (KEEP_UPPER.has(up)) return up;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join('');
}

export const KIND_LABELS: Record<EntityKind, string> = {
  company: 'Company',
  business_name: 'Business name',
  cooperative: 'Cooperative',
  society: 'Society',
  foreign: 'Foreign company',
  unknown: 'Entity',
};

/** `Local Company - Limited by Shares` → `Local company, limited by shares`. */
export function companyTypeLabel(t: string | null | undefined): string | null {
  if (!t) return null;
  const s = t.replace(/\s*-\s*/g, ', ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** `2026-09-19` → `1996` */
export function yearOf(iso: string | null | undefined): string | null {
  return iso && /^\d{4}/.test(iso) ? iso.slice(0, 4) : null;
}

export const PURPOSE_LABELS: Record<Purpose, string> = {
  supplier_onboarding: 'Supplier onboarding',
  credit_application: 'Credit application',
  periodic_review: 'Periodic review',
  other: 'Other',
};

export const CHECK_LABELS: Record<CheckName, string> = {
  registry: 'Registry status',
  tax: 'Tax registration',
  vat: 'VAT registration',
  returns: 'Annual returns',
  licences: 'Licences',
  screening: 'Screening',
};

export const CHECK_ORDER: readonly CheckName[] = [
  'registry',
  'tax',
  'vat',
  'returns',
  'licences',
  'screening',
];

export const STATUS_WORDS: Record<CheckStatus, string> = {
  pass: 'Verified',
  attention: 'Attention',
  fail: 'Failed',
  unknown: 'Unknown',
};

export const SOURCE_LABELS: Record<string, string> = {
  pacra: 'PACRA',
  zra: 'ZRA',
  dira: 'Dira',
  zamra: 'ZAMRA',
  cadastre: 'Mining Cadastre',
  boz: 'Bank of Zambia',
  ncc: 'NCC',
  hpcz: 'HPCZ',
  zta: 'ZTA',
  zicta: 'ZICTA',
  pia: 'PIA',
  sec: 'SEC',
  zica: 'ZICA',
  gazette: 'Gazette',
  ofac: 'OFAC',
  un: 'UN',
  eu: 'EU',
  uk: 'UK',
  wb: 'World Bank',
  afdb: 'AfDB',
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source.toUpperCase();
}

/** `4c1e…9f2a` */
export function shortHash(hex: string): string {
  return hex.length > 12 ? `${hex.slice(0, 4)}…${hex.slice(-4)}` : hex;
}

export function confidenceText(n: number | null | undefined): string {
  return typeof n === 'number' ? n.toFixed(2) : '—';
}

/** `returns_lapsed` → `Returns lapsed` */
export function humanise(code: string): string {
  const s = code.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The one-line human reading of a check's value (mirrors src/verify/format.ts). */
export function checkValueText(
  name: CheckName,
  check: { value: unknown; [k: string]: unknown },
): string {
  const v = check.value;
  switch (name) {
    case 'registry':
      return typeof v === 'string' && v ? v : 'Not on the register';
    case 'tax': {
      const tpin = typeof check['tpin'] === 'string' ? check['tpin'] : null;
      const label =
        typeof check['status_label'] === 'string' ? check['status_label'] : null;
      if (label) {
        // Vocabulary-aware reading (2026-09-22): the label, never the raw enum.
        if (v === 'registration_incomplete') {
          const issued = check['registered_on'];
          return typeof issued === 'string'
            ? `${label} — TPIN issued ${formatDate(issued)}, no tax types`
            : `${label} — no tax types`;
        }
        if (!tpin) return label;
        return check['basis'] === 'owner_tpin'
          ? `${label} · owner's TPIN ${tpin}`
          : `${label} · TPIN ${tpin}`;
      }
      // Records issued before the vocabulary carried the raw status as the value.
      if (typeof v !== 'string' || !v) return 'No tax record';
      const status = v.charAt(0) + v.slice(1).toLowerCase();
      return tpin ? `${status} · TPIN ${tpin}` : status;
    }
    case 'vat':
      return v === true ? 'Registered' : v === false ? 'Not registered' : 'No tax record';
    case 'returns': {
      if (v === 'current') return 'Current';
      const madeUpTo = check['made_up_to'];
      return typeof madeUpTo === 'string'
        ? `Not current — made up to ${formatDate(madeUpTo)}`
        : 'Not current';
    }
    case 'licences':
      return typeof v === 'number' && v > 0 ? plural(v, 'licence record') : 'None found';
    case 'screening': {
      if (v === 'clear') return 'Clear';
      const pending = Array.isArray(check['pending']) ? check['pending'].length : 0;
      const gazette = Array.isArray(check['gazette']) ? check['gazette'].length : 0;
      const n = pending + gazette;
      return `Review pending${n ? ` · ${plural(n, 'item')}` : ''}`;
    }
  }
}

/**
 * The second, explanatory line of a check when it has one (mirrors
 * src/verify/format.ts): the `meaning` the server recorded — the ZRA status
 * vocabulary, the owner-TPIN prompt. Empty for a pass and for records
 * issued before meanings existed.
 */
export function checkMeaningText(check: {
  status: CheckStatus;
  [k: string]: unknown;
}): string {
  if (check.status === 'pass') return '';
  const meaning = check['meaning'];
  return typeof meaning === 'string' ? meaning : '';
}

export function resultHeadline(r: {
  passed: number;
  attention: number;
  failed: number;
  unknown: number;
}): string {
  const total = r.passed + r.attention + r.failed + r.unknown;
  if (total === 0) return 'No checks run';
  return `${r.passed} of ${plural(total, 'check')} passed`;
}

export function resultDetail(r: {
  attention: number;
  failed: number;
  unknown: number;
}): string {
  const out: string[] = [];
  if (r.attention)
    out.push(`${r.attention} need${r.attention === 1 ? 's' : ''} attention`);
  if (r.failed) out.push(`${r.failed} failed`);
  if (r.unknown) out.push(`${r.unknown} unknown`);
  return out.join(' · ');
}

/** Initials for the header avatar: `chola@zanaco.test` → `C`, `Chola Mwansa` → `CM`. */
export function initials(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0]! : nameOrEmail;
  const words = base.split(/[\s._-]+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}
